import React, { useState, useEffect, useRef } from 'react';
import { jsPDF } from 'jspdf';
import QRCode from 'qrcode';
import api from '../api/client';
import '../GetLabelsPage.css';
import ReplacementLabel from './ReplacementLabel';

// ── PDF helpers ───────────────────────────────────────────────────────────────

function formatBagNumber(n) {
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const idx = Math.floor((n - 1) / 9999);
  const num = ((n - 1) % 9999) + 1;
  return `${letters[idx] || 'Z'}${String(num).padStart(4, '0')}`;
}

async function qrToDataUrl(text) {
  const canvas = document.createElement('canvas');
  await QRCode.toCanvas(canvas, text, { margin: 1, width: 220, errorCorrectionLevel: 'M' });
  return canvas.toDataURL('image/png');
}

const LH = (pt) => pt * 0.3528 * 1.08;

// Label: BAG # + number
async function generateBagNumberPDF(labelCount, startNumber) {
  const W = 50;
  const H = 25;
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: [W, H] });

  const qrMargin = 0.75;
  const qrSize   = H - 2 * qrMargin;
  const qrX      = W - qrSize - qrMargin;
  const qrY      = qrMargin;
  const colW     = qrX - 0.5;
  const colCX    = colW / 2;

  const fontSize = 16;
  const lineH    = fontSize * 0.3528 * 1.1;
  const blockH   = lineH * 2;
  const startY   = (H - blockH) / 2 + lineH;

  for (let i = 0; i < labelCount; i++) {
    if (i > 0) pdf.addPage([W, H], 'landscape');
    const bagNum    = formatBagNumber(startNumber + i);
    const qrDataUrl = await qrToDataUrl(bagNum);
    pdf.addImage(qrDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(fontSize);
    pdf.text('BAG #', colCX, startY,          { align: 'center' });
    pdf.text(bagNum,   colCX, startY + lineH, { align: 'center' });
  }

  return pdf;
}

// Label: material name + QR with rawMaterialID
async function generateMaterialIdPDF(materialName, rawMaterialID, qty) {
  const W = 50;
  const H = 25;
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: [W, H] });

  const qrMargin  = 0.75;
  const qrSize    = H - 2 * qrMargin;
  const qrX       = W - qrSize - qrMargin;
  const qrY       = qrMargin;
  const colW      = qrX - 0.5;
  const colCX     = colW / 2;
  const textW     = colW - 1;
  const availH    = H - 2;

  const qrDataUrl = await qrToDataUrl(String(rawMaterialID));

  let namePt = 14;
  let nameLines;
  while (namePt >= 7) {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(namePt);
    nameLines = pdf.splitTextToSize(materialName, textW);
    if (nameLines.length * LH(namePt) <= availH) break;
    namePt -= 0.5;
  }

  const nameTH = nameLines.length * LH(namePt);
  const baseY  = (H - nameTH) / 2 + LH(namePt);

  for (let i = 0; i < qty; i++) {
    if (i > 0) pdf.addPage([W, H], 'landscape');
    pdf.addImage(qrDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(namePt);
    nameLines.forEach((line, idx) => {
      pdf.text(line, colCX, baseY + idx * LH(namePt), { align: 'center' });
    });
  }

  return pdf;
}

// ── Component ─────────────────────────────────────────────────────────────────

const FertBags = () => {
  // ── Section 1: Bag numbering ──────────────────────────────────────────────
  const [bagQty,        setBagQty]        = useState(1);
  const [generatingBag, setGeneratingBag] = useState(false);
  const [bagProgress,   setBagProgress]   = useState(0);
  const [bagFile,       setBagFile]       = useState(null);
  const [bagError,      setBagError]      = useState('');

  const handleGenerateBag = async () => {
    const count = parseInt(bagQty) || 0;
    if (count < 1) return;
    setGeneratingBag(true);
    setBagProgress(0);
    setBagFile(null);
    setBagError('');
    try {
      const res = await api.post('/api/labels/reserve-fert-bags', { count });
      const { startNumber } = res.data.data;
      setBagProgress(30);
      const pdf  = await generateBagNumberPDF(count, startNumber);
      setBagProgress(100);
      const blob = pdf.output('blob');
      const name = count === 1
        ? `FertBag ${formatBagNumber(startNumber)}`
        : `FertBags ${formatBagNumber(startNumber)}–${formatBagNumber(startNumber + count - 1)}`;
      setBagFile({ blob, name });
    } catch (err) {
      setBagError('Error: ' + (err.response?.data?.error || err.message));
    } finally {
      setGeneratingBag(false);
    }
  };

  const downloadBagFile = () => {
    const url = URL.createObjectURL(bagFile.blob);
    const a   = document.createElement('a');
    a.href = url; a.download = `${bagFile.name}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  // ── Section 2: Material ID labels ────────────────────────────────────────
  const [materials,        setMaterials]        = useState([]);
  const [matSearch,        setMatSearch]        = useState('');
  const [showMatDrop,      setShowMatDrop]      = useState(false);
  const [selectedMats,     setSelectedMats]     = useState([]);
  const [generatingMat,    setGeneratingMat]    = useState(false);
  const [matProgress,      setMatProgress]      = useState(0);
  const [matProgLabel,     setMatProgLabel]     = useState('');
  const [matFiles,         setMatFiles]         = useState([]);
  const [matError,         setMatError]         = useState('');

  const matSearchRef   = useRef(null);
  const matSearchInput = useRef(null);

  useEffect(() => {
    api.get('/api/labels/fert-materials')
      .then(r => setMaterials(r.data.data))
      .catch(() => setMatError('Failed to load materials'));
  }, []);

  useEffect(() => {
    const h = (e) => {
      if (matSearchRef.current && !matSearchRef.current.contains(e.target))
        setShowMatDrop(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const filteredMats = materials.filter(
    (m) =>
      !selectedMats.find((i) => i.rawMaterialID === m.rawMaterialID) &&
      ((m.name || '').toLowerCase().includes(matSearch.toLowerCase()) ||
        String(m.rawMaterialID).includes(matSearch))
  );

  const addMat = (mat) => {
    setSelectedMats((prev) => [...prev, { ...mat, qty: 1 }]);
    setMatSearch('');
    setShowMatDrop(true);
    matSearchInput.current?.focus();
  };

  const updateMat = (rawMaterialID, qty) =>
    setSelectedMats((prev) => prev.map((m) => m.rawMaterialID === rawMaterialID ? { ...m, qty } : m));

  const removeMat = (rawMaterialID) =>
    setSelectedMats((prev) => prev.filter((m) => m.rawMaterialID !== rawMaterialID));

  const handleGenerateMat = async () => {
    if (selectedMats.length === 0) return;
    setGeneratingMat(true);
    setMatProgress(0);
    setMatProgLabel('');
    setMatFiles([]);
    setMatError('');
    try {
      const files = [];
      for (let idx = 0; idx < selectedMats.length; idx++) {
        const item = selectedMats[idx];
        setMatProgLabel(`Generating: ${item.name}`);
        const pdf  = await generateMaterialIdPDF(item.name, item.rawMaterialID, item.qty);
        const blob = pdf.output('blob');
        files.push({ name: item.name, rawMaterialID: item.rawMaterialID, blob });
        setMatProgress(Math.round(((idx + 1) / selectedMats.length) * 100));
      }
      setMatFiles(files);
    } catch (err) {
      setMatError('Error: ' + (err.response?.data?.error || err.message));
    } finally {
      setGeneratingMat(false);
      setMatProgLabel('');
    }
  };

  const downloadMatFile = (file) => {
    const url = URL.createObjectURL(file.blob);
    const a   = document.createElement('a');
    a.href = url; a.download = `${file.name}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  const downloadAllMat = () => matFiles.forEach(downloadMatFile);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="gl-content">

      {/* ── Section 1: Bag Numbering ── */}
      <div className="gl-divider" style={{ marginTop: 0, marginBottom: 28 }}>Bag Numbering Labels</div>

      {bagError && <div className="gl-error">{bagError}</div>}

      <div className="gl-seed-qty-row">
        <div className="gl-qty-field">
          <label className="gl-label">Number of labels</label>
          <input
            type="number" min="1"
            className="gl-input-lg"
            value={bagQty}
            onChange={(e) => { setBagQty(e.target.value); setBagFile(null); }}
          />
        </div>
        {!generatingBag && !bagFile && (
          <button
            className="gl-btn gl-btn-primary"
            onClick={handleGenerateBag}
            disabled={!bagQty || parseInt(bagQty) < 1}
          >
            Generate Labels
          </button>
        )}
      </div>

      {generatingBag && (
        <div className="gl-progress-wrap">
          <div className="gl-progress-label">Generating… {bagProgress}%</div>
          <div className="gl-progress-track">
            <div className="gl-progress-fill" style={{ width: `${bagProgress}%` }} />
          </div>
        </div>
      )}

      {bagFile && (
        <div className="gl-done-header">
          <span className="gl-done-label">Ready!</span>
          <button className="gl-btn gl-btn-primary" onClick={downloadBagFile}>Download PDF</button>
          <button className="gl-btn gl-btn-gray" onClick={() => setBagFile(null)}>New Batch</button>
        </div>
      )}

      <ReplacementLabel
        numberLabel="Bag number"
        fileName={(code) => `FertBag ${code} (replacement)`}
        generate={(n) => generateBagNumberPDF(1, n)}
      />

      {/* ── Section 2: Material ID Labels ── */}
      <div className="gl-divider" style={{ margin: '32px 0 28px' }}>Fertilizer Material ID Labels</div>

      {matError && <div className="gl-error">{matError}</div>}

      {/* Material search */}
      <div className="gl-search-wrap" ref={matSearchRef}>
        <label className="gl-label">Add Materials</label>
        <input
          ref={matSearchInput}
          type="text"
          className="gl-input"
          value={matSearch}
          onChange={(e) => { setMatSearch(e.target.value); setShowMatDrop(true); }}
          onFocus={() => setShowMatDrop(true)}
          placeholder="Search by name or ID — click to add, search again for more…"
        />
        {showMatDrop && (matSearch || filteredMats.length > 0) && (
          <div className="gl-dropdown">
            {filteredMats.length === 0 ? (
              <div className="gl-dropdown-empty">
                {matSearch ? 'No materials found' : 'Start typing to search…'}
              </div>
            ) : (
              filteredMats.map((m) => (
                <div
                  key={m.rawMaterialID}
                  className="gl-dropdown-item"
                  onMouseDown={() => addMat(m)}
                >
                  <span style={{ fontWeight: 500 }}>{m.name}</span>
                  <span style={{ marginLeft: 8, fontSize: 12, color: '#888' }}>#{m.rawMaterialID}</span>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Selected materials table */}
      {selectedMats.length > 0 && (
        <div className="gl-table-wrap">
          <table className="gl-table">
            <thead>
              <tr>
                <th className="gl-th">Material</th>
                <th className="gl-th gl-th-w110">ID</th>
                <th className="gl-th gl-th-w120 gl-th-center">Label Count</th>
                <th className="gl-th gl-th-w40 gl-th-center" />
              </tr>
            </thead>
            <tbody>
              {selectedMats.map((item) => (
                <tr key={item.rawMaterialID}>
                  <td className="gl-td">{item.name}</td>
                  <td className="gl-td gl-td-muted">#{item.rawMaterialID}</td>
                  <td className="gl-td gl-td-center">
                    <input
                      type="number" min="1"
                      className="gl-num-input"
                      value={item.qty}
                      onChange={(e) => updateMat(item.rawMaterialID, parseInt(e.target.value) || 1)}
                    />
                  </td>
                  <td className="gl-td gl-td-center">
                    <button className="gl-remove-btn" onClick={() => removeMat(item.rawMaterialID)}>×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedMats.length > 0 && !generatingMat && matFiles.length === 0 && (
        <button className="gl-btn gl-btn-primary gl-btn-generate" onClick={handleGenerateMat}>
          Generate Labels
        </button>
      )}

      {generatingMat && (
        <div className="gl-progress-wrap">
          <div className="gl-progress-label">
            {matProgLabel || 'Generating…'} — {matProgress}%
          </div>
          <div className="gl-progress-track">
            <div className="gl-progress-fill" style={{ width: `${matProgress}%` }} />
          </div>
        </div>
      )}

      {matFiles.length > 0 && (
        <div>
          <div className="gl-done-header">
            <span className="gl-done-label">
              Done! {matFiles.length} file{matFiles.length > 1 ? 's' : ''} ready
            </span>
            {matFiles.length > 1 && (
              <button className="gl-btn gl-btn-primary" onClick={downloadAllMat}>Download All</button>
            )}
            <button className="gl-btn gl-btn-gray" onClick={() => { setMatFiles([]); setMatProgress(0); }}>
              New Batch
            </button>
          </div>
          <div className="gl-files-list">
            {matFiles.map((f, i) => (
              <div key={i} className="gl-file-item">
                <div>
                  <span className="gl-file-name">{f.name}</span>
                  <span className="gl-file-sku">#{f.rawMaterialID}</span>
                </div>
                <button className="gl-btn gl-btn-primary gl-btn-sm" onClick={() => downloadMatFile(f)}>
                  Download PDF
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
};

export default FertBags;
